import { useMemo } from 'react';

import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';

import {
  Configuration,
  ConfigurationsApi,
  CreateConfigurationConfigurationsPostRequest,
  DeleteConfigurationConfigurationsIdDeleteRequest,
  DisableConfigurationConfigurationsIdSetStatusPutRequest,
  GroundingsApi,
  PageConfigurationSchema,
  ReadGroundingsGroundingsGetRequest,
  UpdateConfigurationConfigurationsIdPutRequest,
} from '@ais-ui/api-spec/grounding';
import { getBasePath } from './getAISBasePath';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import { useBasePath } from '@ais-ui/custom-hooks';
import { useSession } from '@ais-ui/websockets';

const useGroundingRequestConfiguration = () => {
  const credentials = useCredentials();
  const basePath = getBasePath();
  const { hostname } = useBasePath();
  const username = credentials?.username || '';
  const password = credentials?.password || '';
  const { sessionId } = useSession();

  if (hostname.includes('localhost')) {
    const authHeader = getAuthHeaderFromCredentials(username, password);
    return new Configuration({
      basePath: `${basePath}api/ingress/grounding`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    return new Configuration({
      basePath: `${basePath}aisweb/api/ingress/grounding`,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }
};

export const useGroundingApi = () => {
  const configuration = useGroundingRequestConfiguration();
  const apiClientWrapper = useApiClientWrapper();
  const groundingApi = useMemo(
    () => new GroundingsApi(configuration),
    [configuration]
  );
  return apiClientWrapper(groundingApi) as GroundingsApi;
};

export const useGroundingConfigurationApi = () => {
  const configuration = useGroundingRequestConfiguration();
  const groundingApi = useMemo(
    () => new ConfigurationsApi(configuration),
    [configuration]
  );
  return groundingApi;
};

export const useGetGroundingsWithDateTimes = (
  requestParams: ReadGroundingsGroundingsGetRequest,
  enabled = true
) => {
  const groundingApi = useGroundingApi();

  return useQuery({
    queryKey: ['groundings', requestParams],
    queryFn: async () => {
      const response = await groundingApi.readGroundingsGroundingsGet(
        requestParams
      );

      convertFieldsToDates(response, [
        'items.createdAt',
        'items.modifiedAt',
        'items.groundingTime',
        'items.releaseTime',
      ]);

      return response;
    },
    enabled,
  }, 'grounding:list');
};

export const usePostGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestBody: CreateConfigurationConfigurationsPostRequest) => {
      return groundingConfigurationsApi.createConfigurationConfigurationsPost(
        requestBody
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundingAllDefinitions'] });
    },
  }, 'grounding:create');
};

export const useFetchAllGroundingConfigurationDefinitions = (
  enabled = true
): UseQueryResult<PageConfigurationSchema> => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();

  return useQuery({
    queryKey: ['groundingAllDefinitions'],
    queryFn: async () => {
      return await groundingConfigurationsApi.readConfigurationsConfigurationsGet();
    },
    enabled,
  }, 'grounding:listConfigurations');
};

export const useDisableGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      requestBody: DisableConfigurationConfigurationsIdSetStatusPutRequest
    ) => {
      return groundingConfigurationsApi.disableConfigurationConfigurationsIdSetStatusPut(
        requestBody
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundingAllDefinitions'] });
    },
  }, 'grounding:disable');
};

export const useDeleteGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      requestBody: DeleteConfigurationConfigurationsIdDeleteRequest
    ) => {
      return groundingConfigurationsApi.deleteConfigurationConfigurationsIdDelete(
        requestBody
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundingAllDefinitions'] });
    },
  }, 'grounding:delete');
};

export const useEditGroundingConfiguration = () => {
  const groundingConfigurationsApi = useGroundingConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      requestBody: UpdateConfigurationConfigurationsIdPutRequest
    ) => {
      return groundingConfigurationsApi.updateConfigurationConfigurationsIdPut(
        requestBody
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundingAllDefinitions'] });
    },
  }, 'grounding:update');
};
